/** State, effects, and handlers for the ticket table. */

import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useTenant } from '@/hooks/useTenant';
import { toast } from 'sonner';
import { REALTIME_LIST_DEBOUNCE_MS, useRecordUpdated } from '@/hooks/useRecordUpdated';
import type { RecordUpdatedPayload } from '@/lib/realtime/types';
import { buildActionApiRequest } from '@/lib/utils/actionApiUtils';
import { convertGMTtoIST } from '@/lib/utils/timeUtils';

import type { Column, TicketTableProps } from './types';
import {
  TICKET_API_BASE,
  defaultColumns,
  transformTicketForCarousel,
  getDisplayName,
} from './utils';

export function useTicketTable({ config }: TicketTableProps) {
const [data, setData] = useState<any[]>([]);
const [filteredData, setFilteredData] = useState<any[]>([]);
const [loading, setLoading] = useState(true);
const [tableLoading, setTableLoading] = useState(false); // Separate loading state for table only
const [searchLoading, setSearchLoading] = useState(false); // Loading state specifically for search
const [rateLimited, setRateLimited] = useState(false); // Rate limiting indicator
const [selectedTicket, setSelectedTicket] = useState<any>(null);
const [isTicketModalOpen, setIsTicketModalOpen] = useState(false);
const [showFilters, setShowFilters] = useState(false);
const [resolutionStatusFilter, setResolutionStatusFilter] = useState<string[]>([]);
const [assignedToFilter, setAssignedToFilter] = useState<string>('all');
const [posterStatusFilter, setPosterStatusFilter] = useState<string[]>([]);
const [stateFilter, setStateFilter] = useState<string[]>([]);
const [callAttemptsFilter, setCallAttemptsFilter] = useState<number[]>([]);
const [dateRangeFilter, setDateRangeFilter] = useState<{
  startDate: Date | undefined;
  endDate: Date | undefined;
  startTime: string;
  endTime: string;
}>({
  startDate: undefined,
  endDate: undefined,
  startTime: '00:00',
  endTime: '23:59'
});
const [apiPrefix, setApiPrefix] = useState<'supabase' | 'renderer'>(
  config?.apiPrefix || 'renderer'
);
const [filtersApplied, setFiltersApplied] = useState(false);
const [searchTerm, setSearchTerm] = useState<string>('');
const [displaySearchTerm, setDisplaySearchTerm] = useState<string>(''); // Separate state for display
const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
const abortControllerRef = useRef<AbortController | null>(null);
const listFetchInFlightRef = useRef(false);
const createdRefreshTimerRef = useRef<number | null>(null);
const requestSequenceRef = useRef<number>(0);
/** Base data for client-side search (initial load or filter result). Search runs across all fields on this. */
const baseDataRef = useRef<any[]>([]);
const [pagination, setPagination] = useState<{
  totalCount: number;
  numberOfPages: number;
  currentPage: number;
  pageSize: number;
  nextPageLink: string | null;
  previousPageLink: string | null;
}>({
  totalCount: 0,
  numberOfPages: 0,
  currentPage: 1,
  pageSize: 10,
  nextPageLink: null,
  previousPageLink: null
});
const paginationRef = useRef(pagination);
paginationRef.current = pagination;
const [assignees, setAssignees] = useState<Array<{
  id: number;
  name: string;
  email: string;
  company_name: string | null;
  uid: string | null;
}>>([]);
const [filterOptions, setFilterOptions] = useState<{
  resolution_statuses: (string | null)[];
  poster_statuses: string[];
}>({
  resolution_statuses: [],
  poster_statuses: []
});
const { session, user } = useAuth();
const { customRole, membershipLoaded } = useTenant();
// CSE users only see tickets after applying at least one filter (search alone doesn't count).
const requireFiltersToShow = customRole?.toUpperCase() === 'CSE';
const hasSelectedFilters =
  resolutionStatusFilter.length > 0 ||
  assignedToFilter !== 'all' ||
  posterStatusFilter.length > 0 ||
  stateFilter.length > 0 ||
  callAttemptsFilter.length > 0 ||
  !!dateRangeFilter.startDate ||
  !!dateRangeFilter.endDate;

const clearTicketList = () => {
  baseDataRef.current = [];
  setData([]);
  setFilteredData([]);
  setPagination({
    totalCount: 0,
    numberOfPages: 0,
    currentPage: 1,
    pageSize: 50,
    nextPageLink: null,
    previousPageLink: null,
  });
};

// Memoize table columns to prevent unnecessary re-renders
const tableColumns: Column[] = useMemo(() => 
  config?.columns?.map(col => ({
    header: col.label,
    accessor: col.key,
    type: col.type === 'chip' ? 'chip' : col.type === 'link' ? 'link' : col.type === 'action' ? 'action' : 'text',
    openCard: col.openCard,
    actionApiEndpoint: col.actionApiEndpoint,
    actionApiMethod: col.actionApiMethod,
    actionApiHeaders: col.actionApiHeaders,
    actionApiPayload: col.actionApiPayload,
  })) || defaultColumns,
  [config?.columns]
);

/** Client-side search: match term against ALL ticket fields (table columns + any other field in the row) */
const matchRowBySearchTerm = useCallback((row: any, term: string, _columns: Column[]): boolean => {
  if (!term || !term.trim()) return true;
  const lower = term.trim().toLowerCase();
  // Search every field in the row so "all" fields are included
  for (const key of Object.keys(row)) {
    const val = row[key];
    if (val == null) continue;
    if (typeof val === 'object' && val !== null && !Array.isArray(val)) continue; // skip nested objects
    if (Array.isArray(val)) {
      if (val.some((v: any) => v != null && String(v).toLowerCase().includes(lower))) return true;
      continue;
    }
    if (String(val).toLowerCase().includes(lower)) return true;
  }
  return false;
}, []);

// Get unique values for filters
const getUniqueResolutionStatuses = () => {
  // Use API data if available, otherwise fallback to local data
  if (filterOptions.resolution_statuses.length > 0) {
    return filterOptions.resolution_statuses;
  }
  
  // Fallback to local data
  const statuses = [...new Set(data.map(ticket => ticket.resolution_status))];
  return statuses.filter(status => status && status !== 'N/A');
};

// Fetch filter options from API
const fetchFilterOptions = async () => {
  try {
    const authToken = session?.access_token;
    const baseUrl = TICKET_API_BASE;
    const apiUrl = `${baseUrl}/analytics/support-tickets/filter-options/`;
    
    console.log('Fetching filter options from:', apiUrl);
    console.log('Auth token:', authToken ? 'Present' : 'Missing');
    
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Authorization': authToken ? `Bearer ${authToken}` : '',
        'Content-Type': 'application/json'
      }
    });

    console.log('Response status:', response.status);
    console.log('Response headers:', Object.fromEntries(response.headers.entries()));

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error response:', errorText);
      throw new Error(`Failed to fetch filter options: ${response.status} - ${errorText}`);
    }

    const responseData = await response.json();
    console.log('Filter options response:', responseData);
    
    if (responseData.resolution_statuses && responseData.poster_statuses) {
      setFilterOptions({
        resolution_statuses: responseData.resolution_statuses,
        poster_statuses: responseData.poster_statuses
      });
    } else {
      console.error('Invalid filter options data format:', responseData);
      setFilterOptions({
        resolution_statuses: [],
        poster_statuses: []
      });
    }
  } catch (error) {
    console.error('Error fetching filter options:', error);
    setFilterOptions({
      resolution_statuses: [],
      poster_statuses: []
    });
  }
};

// Debounced function to fetch filter options with search


// Fetch assignees from API
const fetchAssignees = async () => {
  try {
    const authToken = session?.access_token;
    const baseUrl = TICKET_API_BASE;
    const apiUrl = `${baseUrl}/accounts/users/assignees-by-role/?role=CSE`;
    
    console.log('Fetching assignees from:', apiUrl);
    console.log('Auth token:', authToken ? 'Present' : 'Missing');
    
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Authorization': authToken ? `Bearer ${authToken}` : '',
        'Content-Type': 'application/json'
      }
    });

    console.log('Response status:', response.status);
    console.log('Response headers:', Object.fromEntries(response.headers.entries()));

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error response:', errorText);
      throw new Error(`Failed to fetch assignees: ${response.status} - ${errorText}`);
    }

    const responseData = await response.json();
    console.log('Assignees response:', responseData);
    
    if (responseData.results && Array.isArray(responseData.results)) {
      setAssignees(responseData.results);
    } else {
      console.error('Invalid assignees data format:', responseData);
      setAssignees([]);
    }
  } catch (error) {
    console.error('Error fetching assignees:', error);
    setAssignees([]);
  }
};

const getUniqueAssignedTo = () => {
  // Return the assignees fetched from API
  return assignees.map(assignee => ({
    id: assignee.uid || assignee.id.toString(), // Use uid if available, fallback to id
    name: assignee.name
  }));
};

const getUniquePosterStatuses = () => {
  // Use API data if available, otherwise fallback to local data
  if (filterOptions.poster_statuses.length > 0) {
    return filterOptions.poster_statuses;
  }
  
  // Fallback to local data
  const statuses = [...new Set(data.map(ticket => ticket.poster))];
  return statuses.filter(status => status && status !== 'N/A' && status !== 'No Poster');
};

const stateToParamValue = (state: string | null | undefined): string => {
  if (state == null || String(state).trim() === '') return 'null';
  return String(state);
};

// Apply filters using analytics endpoint
const applyFilters = async (
  requestSequence?: number,
  options?: { silent?: boolean; keepPage?: boolean },
) => {
  const silent = options?.silent === true;
  if (silent && listFetchInFlightRef.current) {
    return;
  }
  if (requireFiltersToShow && !hasSelectedFilters) {
    if (!silent) {
      clearTicketList();
      setFiltersApplied(false);
      toast.info('Select at least one filter to see tickets');
    }
    return;
  }

  const abortController = new AbortController();
  listFetchInFlightRef.current = true;
  try {
    if (!silent) setTableLoading(true);

    if (!silent && abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    if (!silent) {
      abortControllerRef.current = abortController;
    }
    
    // Use provided sequence or increment for non-search calls
    const currentSequence = requestSequence || ++requestSequenceRef.current;
    
    const authToken = session?.access_token;

    // Always use renderer URL for analytics endpoint
    const baseUrl = TICKET_API_BASE;
    const apiUrl = `${baseUrl}/analytics/support-ticket/`;
    
    // Build query parameters
    const params = new URLSearchParams();
    
    // Add resolution status filters
    if (resolutionStatusFilter.length > 0) {
      resolutionStatusFilter.forEach(status => {
        // Send null for "Open" status, otherwise send the status as-is
        const statusToSend = status === 'Open' ? 'null' : status;
        params.append('resolution_status', statusToSend);
      });
    }
    
    // Add assigned to filter
    if (assignedToFilter !== 'all') {
      if (assignedToFilter === 'myself') {
        params.append('assigned_to', user?.id || '');
      } else if (assignedToFilter === 'unassigned') {
        params.append('assigned_to', 'null');
      } else {
        // For specific assignee, use the ID directly
        params.append('assigned_to', assignedToFilter);
      }
    }
    
    // Add poster status filters
    if (posterStatusFilter.length > 0) {
      posterStatusFilter.forEach(status => {
        params.append('poster', status);
      });
    }

    if (stateFilter.length > 0) {
      stateFilter.forEach((state) => params.append('state', state));
    }

    if (callAttemptsFilter.length > 0) {
      callAttemptsFilter.forEach((count) => params.append('call_attempts', String(count)));
    }
    
    // Add date range filters
    if (dateRangeFilter.startDate) {
      const startDateTime = new Date(dateRangeFilter.startDate);
      startDateTime.setHours(parseInt(dateRangeFilter.startTime.split(':')[0]), parseInt(dateRangeFilter.startTime.split(':')[1]));
      params.append('created_at__gte', startDateTime.toISOString());
    }
    if (dateRangeFilter.endDate) {
      const endDateTime = new Date(dateRangeFilter.endDate);
      endDateTime.setHours(parseInt(dateRangeFilter.endTime.split(':')[0]), parseInt(dateRangeFilter.endTime.split(':')[1]));
      params.append('created_at__lte', endDateTime.toISOString());
    }

    // Include search param for backend search (improves time and accuracy)
    const currentSearchTerm = latestSearchValueRef.current?.trim() ?? '';
    const isSearching = currentSearchTerm !== '';
    if (currentSearchTerm) {
      params.append('search', currentSearchTerm);
      if (config?.searchFields) {
        params.append('search_fields', config.searchFields);
      }
    }
    
    // Pagination: keep the user's current page on silent realtime refresh
    const page = options?.keepPage
      ? String(paginationRef.current.currentPage || 1)
      : '1';
    params.append('page', page);
    params.append('page_size', '50');
    
    const fullUrl = `${apiUrl}?${params.toString()}`;
    console.log('Filtered API URL:', fullUrl);
    console.log('Resolution status filter mapping:', resolutionStatusFilter.map(status => ({
      original: status,
      sent: status === 'Open' ? 'null' : status
    })));

    const response = await fetch(fullUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authToken ? `Bearer ${authToken}` : ''
      },
      signal: abortController.signal
    });

    if (!response.ok) {
      if (response.status === 429) {
        throw new Error(`Rate limit exceeded. Please wait a moment before searching again.`);
      }
      throw new Error(`Failed to fetch filtered tickets: ${response.status}`);
    }

    const responseData = await response.json();
    
    // Check if this response is still relevant (not superseded by a newer request)
    if (currentSequence !== requestSequenceRef.current) {
      console.log(`Ignoring stale response for sequence ${currentSequence}, current is ${requestSequenceRef.current}, search term was: "${searchTerm}"`);
      return;
    }
    
    console.log(`Processing response for sequence ${currentSequence}, search term: "${searchTerm}", display term: "${displaySearchTerm}", tickets found: ${responseData.data?.length ?? responseData.results?.length ?? (Array.isArray(responseData) ? responseData.length : '?')}`);
    
    // Handle different response formats (array or single object)
    let tickets: any[] = [];
    let pageMeta = null;
    
    const ensureArray = (val: any): any[] => Array.isArray(val) ? val : val != null && typeof val === 'object' ? [val] : [];
    
    if (responseData.results !== undefined) {
      tickets = ensureArray(responseData.results);
      pageMeta = responseData.page_meta;
    } else if (responseData.data !== undefined) {
      tickets = ensureArray(responseData.data);
      pageMeta = responseData.page_meta;
    } else if (Array.isArray(responseData)) {
      tickets = responseData;
    } else {
      throw new Error('Invalid data format received');
    }

    // Transform the data with the new attributes
    const transformedData = tickets.map((ticket: any) => ({
      ...ticket,
      // Format created_at with relative time
      created_at: ticket.created_at ? convertGMTtoIST(ticket.created_at) : 'N/A',
      // Use cse_name for assigned to with display name
      cse_name: getDisplayName(ticket.cse_name || ticket.assigned_to),
      // Combine first_name and last_name for name
      name: ticket.first_name && ticket.last_name 
        ? `${ticket.first_name} ${ticket.last_name}`
        : ticket.name || 'N/A',
      // Use reason field
      reason: ticket.reason || ticket.Description || 'No reason provided',
      // Use resolution_status with proper formatting
      resolution_status: ticket.resolution_status || ticket.status || 'Open',
      // Use poster field directly
      poster: ticket.poster || 'No Poster',
      // Generate Praja dashboard user link
      praja_dashboard_user_link: ticket.praja_user_id 
        ? `https://app.praja.com/dashboard/user/${ticket.praja_user_id}`
        : ticket.praja_dashboard_user_link || 'N/A',
      // Ensure display_pic_url is included
      display_pic_url: ticket.display_pic_url || null
    }));

    baseDataRef.current = transformedData;
    // Backend already applies search when search param is sent - no client-side filter needed
    const toShow = transformedData;
    console.log(`Setting filtered data for sequence ${currentSequence}: ${transformedData.length} from API (search: "${currentSearchTerm}")`);
    setFilteredData(toShow);
    setFiltersApplied(true);
    
    // Update pagination data if available
    if (pageMeta) {
      setPagination({
        totalCount: pageMeta.total_count || 0,
        numberOfPages: pageMeta.number_of_pages || 0,
        currentPage: pageMeta.current_page || 1,
        pageSize: pageMeta.page_size || 50,
        nextPageLink: pageMeta.next_page_link || null,
        previousPageLink: pageMeta.previous_page_link || null
      });
      console.log('Updated pagination from filtered response:', pageMeta);
    }
  } catch (error: unknown) {
    // Don't show error for aborted requests
    const err = error as { name?: string; message?: string };
    if (err.name === 'AbortError') {
      console.log('Request was aborted');
      return;
    }
    console.error('Error applying filters:', error);
    
    // Handle different types of errors
    if (err.message?.includes('Rate limit exceeded')) {
      toast.error('Too many requests. Please wait a moment before searching again.');
    } else if (err.message?.includes('429')) {
      toast.error('Rate limit exceeded. Please wait before making another request.');
    } else {
      toast.error('Failed to apply filters');
    }
  } finally {
    listFetchInFlightRef.current = false;
    if (!silent && abortControllerRef.current === abortController) {
      abortControllerRef.current = null;
    }
    if (!silent) setTableLoading(false);
  }
};

// Reset filters
const resetFilters = () => {
  setResolutionStatusFilter([]);
  setAssignedToFilter('all');
  setPosterStatusFilter([]);
  setStateFilter([]);
  setCallAttemptsFilter([]);
  setDateRangeFilter({
    startDate: undefined,
    endDate: undefined,
    startTime: '00:00',
    endTime: '23:59'
  });
  setSearchTerm(''); // Clear search term
  setDisplaySearchTerm(''); // Clear display search term
  if (searchTimeoutRef.current) {
    clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = null;
  }
  latestSearchValueRef.current = '';
  setFiltersApplied(false);
  if (requireFiltersToShow) {
    clearTicketList();
    return;
  }
  baseDataRef.current = data;
  setFilteredData(data); // Show all tickets again
};

const latestSearchValueRef = useRef<string>('');

// Search: simplified and optimized
const debouncedSearch = useCallback((value: string) => {
  latestSearchValueRef.current = value;
  setDisplaySearchTerm(value);

  // Cancel previous search timeout
  if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
  
  // Cancel any ongoing API requests
  if (abortControllerRef.current) {
    abortControllerRef.current.abort();
  }

  searchTimeoutRef.current = setTimeout(async () => {
    const term = latestSearchValueRef.current.trim();
    setSearchTerm(term);
    
    // Create new abort controller for this search
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    // Check if filters are applied
    const hasFilters = filtersApplied || 
      resolutionStatusFilter.length > 0 || 
      assignedToFilter !== 'all' || 
      posterStatusFilter.length > 0 ||
      stateFilter.length > 0 ||
      callAttemptsFilter.length > 0 ||
      dateRangeFilter.startDate ||
      dateRangeFilter.endDate;

    if (requireFiltersToShow && !hasFilters) {
      setSearchLoading(false);
      return;
    }
    
    // If search was cleared, reset to original data
    if (!term) {
      setSearchLoading(false); // Clear search loading when search is cleared
      if (hasFilters) {
        // If filters are applied, re-apply filters without search
        applyFilters();
      } else {
        // Reset to original first page (50 tickets)
        try {
          setTableLoading(true);
          const authToken = session?.access_token;
          const baseUrl = TICKET_API_BASE;
          const apiUrl = `${baseUrl}/analytics/support-ticket/`;
          
          const params = new URLSearchParams();
          params.append('page', '1');
          params.append('page_size', '50');
          
          const response = await fetch(`${apiUrl}?${params.toString()}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': authToken ? `Bearer ${authToken}` : ''
            },
            signal: abortController.signal
          });

          if (abortController.signal.aborted) return;

          if (response.ok) {
            const responseData = await response.json();
            const ensureArray = (val: any): any[] => Array.isArray(val) ? val : val != null && typeof val === 'object' ? [val] : [];
            
            let tickets: any[] = [];
            let pageMeta = null;
            
            if (responseData.results !== undefined) {
              tickets = ensureArray(responseData.results);
              pageMeta = responseData.page_meta;
            } else if (responseData.data !== undefined) {
              tickets = ensureArray(responseData.data);
              pageMeta = responseData.page_meta;
            } else if (Array.isArray(responseData)) {
              tickets = responseData;
            }

            const transformedData = tickets.map((ticket: any) => ({
              ...ticket,
              created_at: ticket.created_at ? convertGMTtoIST(ticket.created_at) : 'N/A',
              cse_name: getDisplayName(ticket.cse_name || ticket.assigned_to),
              name: ticket.first_name && ticket.last_name 
                ? `${ticket.first_name} ${ticket.last_name}`
                : ticket.name || 'N/A',
              reason: ticket.reason || ticket.Description || 'No reason provided',
              resolution_status: ticket.resolution_status || ticket.status || 'Open',
              poster: ticket.poster || 'No Poster',
              praja_dashboard_user_link: ticket.praja_user_id 
                ? `https://app.praja.com/dashboard/user/${ticket.praja_user_id}`
                : ticket.praja_dashboard_user_link || 'N/A',
              display_pic_url: ticket.display_pic_url || null
            }));

            setData(transformedData);
            baseDataRef.current = transformedData;
            setFilteredData(transformedData);
            
            if (pageMeta) {
              setPagination({
                totalCount: pageMeta.total_count || 0,
                numberOfPages: pageMeta.number_of_pages || 0,
                currentPage: pageMeta.current_page || 1,
                pageSize: 50,
                nextPageLink: pageMeta.next_page_link || null,
                previousPageLink: pageMeta.previous_page_link || null
              });
            }
          }
        } catch (error: any) {
          if (error.name === 'AbortError') return;
          console.error('Error resetting search:', error);
          baseDataRef.current = data;
          setFilteredData(data);
        } finally {
          if (!abortController.signal.aborted) {
            setTableLoading(false);
          }
        }
      }
      return;
    }
    
    // If searching and no filters applied: use backend search (single API call)
    if (term && !hasFilters) {
      try {
        setTableLoading(true);
        setSearchLoading(true);
        const authToken = session?.access_token;
        const baseUrl = TICKET_API_BASE;
        const apiUrl = `${baseUrl}/analytics/support-ticket/`;

        const params = new URLSearchParams();
        params.append('search', term);
        if (config?.searchFields) params.append('search_fields', config.searchFields);
        params.append('page', '1');
        params.append('page_size', '50');

        const response = await fetch(`${apiUrl}?${params.toString()}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken ? `Bearer ${authToken}` : ''
          },
          signal: abortController.signal
        });

        if (abortController.signal.aborted) return;

        const ensureArray = (val: any): any[] => Array.isArray(val) ? val : val != null && typeof val === 'object' ? [val] : [];
        const transformTicket = (ticket: any) => ({
          ...ticket,
          created_at: ticket.created_at ? convertGMTtoIST(ticket.created_at) : 'N/A',
          cse_name: getDisplayName(ticket.cse_name || ticket.assigned_to),
          name: ticket.first_name && ticket.last_name
            ? `${ticket.first_name} ${ticket.last_name}`
            : ticket.name || 'N/A',
          reason: ticket.reason || ticket.Description || 'No reason provided',
          resolution_status: ticket.resolution_status || ticket.status || 'Open',
          poster: ticket.poster || 'No Poster',
          praja_dashboard_user_link: ticket.praja_user_id
            ? `https://app.praja.com/dashboard/user/${ticket.praja_user_id}`
            : ticket.praja_dashboard_user_link || 'N/A',
          display_pic_url: ticket.display_pic_url || null
        });

        if (response.ok) {
          const responseData = await response.json();
          let tickets: any[] = [];
          if (responseData.results !== undefined) tickets = ensureArray(responseData.results);
          else if (responseData.data !== undefined) tickets = ensureArray(responseData.data);
          else if (Array.isArray(responseData)) tickets = responseData;

          const transformedData = tickets.map(transformTicket);
          baseDataRef.current = transformedData;
          setFilteredData(transformedData);

          const pageMeta = responseData.page_meta;
          if (pageMeta) {
            setPagination({
              totalCount: pageMeta.total_count || 0,
              numberOfPages: pageMeta.number_of_pages || 0,
              currentPage: pageMeta.current_page || 1,
              pageSize: 50,
              nextPageLink: pageMeta.next_page_link || null,
              previousPageLink: pageMeta.previous_page_link || null
            });
          }
        } else {
          throw new Error(`Search failed: ${response.status}`);
        }
      } catch (error: any) {
        if (error.name === 'AbortError') return;
        console.error('Error fetching tickets for search:', error);
        const base = baseDataRef.current.length > 0 ? baseDataRef.current : data;
        const next = base.filter((row: any) => matchRowBySearchTerm(row, term, tableColumns));
        setFilteredData(next);
      } finally {
        if (!abortController.signal.aborted) {
          setTableLoading(false);
          setSearchLoading(false);
        }
      }
    } else {
      // Search with filters applied - use backend (applyFilters includes search param)
      setSearchLoading(true);
      try {
        await applyFilters();
      } finally {
        setSearchLoading(false);
      }
    }
  }, 500); // Increased debounce to 500ms to reduce API calls
}, [data, matchRowBySearchTerm, tableColumns, filtersApplied, resolutionStatusFilter, assignedToFilter, posterStatusFilter, stateFilter, callAttemptsFilter, dateRangeFilter, session?.access_token, config?.searchFields, requireFiltersToShow]);

// Handle search input change from PrajaTable
const handleSearchChange = useCallback((value: string) => {
  debouncedSearch(value);
}, [debouncedSearch]);


// Memoized row click handler
const handleRowClick = useCallback((row: any) => {
  setSelectedTicket(transformTicketForCarousel(row));
  setIsTicketModalOpen(true);
}, []);

// Action button click: open card and/or call API
const handleActionClick = useCallback(async (row: any, col: Column) => {
  const openCard = col.openCard === true || col.openCard === 'true';
  if (openCard) {
    setSelectedTicket(transformTicketForCarousel(row));
    setIsTicketModalOpen(true);
  }
  if (col.actionApiEndpoint?.trim()) {
    try {
      const baseUrl = TICKET_API_BASE;
      const { url, method, headers, body } = buildActionApiRequest(
        {
          endpoint: col.actionApiEndpoint,
          method: col.actionApiMethod,
          headers: col.actionApiHeaders,
          payload: col.actionApiPayload,
        },
        row,
        baseUrl,
        {
          'Content-Type': 'application/json',
          'Authorization': session?.access_token ? `Bearer ${session.access_token}` : ''
        },
        'ticket_id'
      );
      const res = await fetch(url, { method, headers, body });
      if (!res.ok) throw new Error(`API error: ${res.status}`);
      toast.success('Action completed');
    } catch (err: any) {
      toast.error(err?.message || 'Action failed');
    }
  }
}, [session?.access_token]);

// Handle pagination navigation
const handleNextPage = async () => {
  if (pagination.nextPageLink) {
    try {
      setTableLoading(true);
      const authToken = session?.access_token;
      
      const response = await fetch(pagination.nextPageLink, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authToken ? `Bearer ${authToken}` : ''
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch next page: ${response.status}`);
      }

      const responseData = await response.json();
      
      let tickets = [];
      let pageMeta = null;
      
      if (responseData.data && Array.isArray(responseData.data)) {
        tickets = responseData.data;
        pageMeta = responseData.page_meta;
      } else {
        throw new Error('Invalid data format received');
      }

      // Transform the data
      const transformedData = tickets.map((ticket: any) => ({
        ...ticket,
        created_at: ticket.created_at ? convertGMTtoIST(ticket.created_at) : 'N/A',
        cse_name: getDisplayName(ticket.cse_name || ticket.assigned_to),
        name: ticket.first_name && ticket.last_name 
          ? `${ticket.first_name} ${ticket.last_name}`
          : ticket.name || 'N/A',
        reason: ticket.reason || ticket.Description || 'No reason provided',
        resolution_status: ticket.resolution_status || ticket.status || 'Open',
        poster: ticket.poster || 'No Poster',
        praja_dashboard_user_link: ticket.praja_user_id 
          ? `https://app.praja.com/dashboard/user/${ticket.praja_user_id}`
          : ticket.praja_dashboard_user_link || 'N/A',
        display_pic_url: ticket.display_pic_url || null
      }));

      setData(transformedData);
      setFilteredData(transformedData);
      
      if (pageMeta) {
        setPagination({
          totalCount: pageMeta.total_count || 0,
          numberOfPages: pageMeta.number_of_pages || 0,
          currentPage: pageMeta.current_page || 1,
          pageSize: pageMeta.page_size || 50,
          nextPageLink: pageMeta.next_page_link || null,
          previousPageLink: pageMeta.previous_page_link || null
        });
      }
    } catch (error) {
      console.error('Error fetching next page:', error);
      toast.error('Failed to load next page');
    } finally {
      setTableLoading(false);
    }
  }
};

const handlePreviousPage = async () => {
  if (pagination.previousPageLink) {
    try {
      setTableLoading(true);
      const authToken = session?.access_token;
      
      const response = await fetch(pagination.previousPageLink, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authToken ? `Bearer ${authToken}` : ''
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch previous page: ${response.status}`);
      }

      const responseData = await response.json();
      
      let tickets = [];
      let pageMeta = null;
      
      if (responseData.data && Array.isArray(responseData.data)) {
        tickets = responseData.data;
        pageMeta = responseData.page_meta;
      } else {
        throw new Error('Invalid data format received');
      }

      // Transform the data
      const transformedData = tickets.map((ticket: any) => ({
        ...ticket,
        created_at: ticket.created_at ? convertGMTtoIST(ticket.created_at) : 'N/A',
        cse_name: getDisplayName(ticket.cse_name || ticket.assigned_to),
        name: ticket.first_name && ticket.last_name 
          ? `${ticket.first_name} ${ticket.last_name}`
          : ticket.name || 'N/A',
        reason: ticket.reason || ticket.Description || 'No reason provided',
        resolution_status: ticket.resolution_status || ticket.status || 'Open',
        poster: ticket.poster || 'No Poster',
        praja_dashboard_user_link: ticket.praja_user_id 
          ? `https://app.praja.com/dashboard/user/${ticket.praja_user_id}`
          : ticket.praja_dashboard_user_link || 'N/A',
        display_pic_url: ticket.display_pic_url || null
      }));

      setData(transformedData);
      setFilteredData(transformedData);
      
      if (pageMeta) {
        setPagination({
          totalCount: pageMeta.total_count || 0,
          numberOfPages: pageMeta.number_of_pages || 0,
          currentPage: pageMeta.current_page || 1,
          pageSize: pageMeta.page_size || 50,
          nextPageLink: pageMeta.next_page_link || null,
          previousPageLink: pageMeta.previous_page_link || null
        });
      }
    } catch (error) {
      console.error('Error fetching previous page:', error);
      toast.error('Failed to load previous page');
    } finally {
      setTableLoading(false);
    }
  }
};

// Handle page selection from dropdown
const handlePageChange = async (pageNumber: string) => {
  const page = parseInt(pageNumber, 10);
  if (isNaN(page) || page < 1 || page > pagination.numberOfPages) {
    return;
  }

  try {
    setTableLoading(true);
    const authToken = session?.access_token;

    // Always use renderer URL for analytics endpoint
    const baseUrl = TICKET_API_BASE;
    const apiUrl = `${baseUrl}/analytics/support-ticket/`;
    
    // Build query parameters with all current filters
    const params = new URLSearchParams();
    
    // Add resolution status filters
    if (resolutionStatusFilter.length > 0) {
      resolutionStatusFilter.forEach(status => {
        const statusToSend = status === 'Open' ? 'null' : status;
        params.append('resolution_status', statusToSend);
      });
    }
    
    // Add assigned to filter
    if (assignedToFilter !== 'all') {
      if (assignedToFilter === 'myself') {
        params.append('assigned_to', user?.id || '');
      } else if (assignedToFilter === 'unassigned') {
        params.append('assigned_to', 'null');
      } else {
        params.append('assigned_to', assignedToFilter);
      }
    }
    
    // Add poster status filters
    if (posterStatusFilter.length > 0) {
      posterStatusFilter.forEach(status => {
        params.append('poster', status);
      });
    }

    if (stateFilter.length > 0) {
      stateFilter.forEach((state) => params.append('state', state));
    }

    if (callAttemptsFilter.length > 0) {
      callAttemptsFilter.forEach((count) => params.append('call_attempts', String(count)));
    }
    
    // Add date range filters
    if (dateRangeFilter.startDate) {
      const startDateTime = new Date(dateRangeFilter.startDate);
      startDateTime.setHours(parseInt(dateRangeFilter.startTime.split(':')[0]), parseInt(dateRangeFilter.startTime.split(':')[1]));
      params.append('created_at__gte', startDateTime.toISOString());
    }
    if (dateRangeFilter.endDate) {
      const endDateTime = new Date(dateRangeFilter.endDate);
      endDateTime.setHours(parseInt(dateRangeFilter.endTime.split(':')[0]), parseInt(dateRangeFilter.endTime.split(':')[1]));
      params.append('created_at__lte', endDateTime.toISOString());
    }

    const currentSearchTerm = latestSearchValueRef.current?.trim() ?? '';
    if (currentSearchTerm) {
      params.append('search', currentSearchTerm);
      if (config?.searchFields) params.append('search_fields', config.searchFields);
    }

    params.append('page', page.toString());
    params.append('page_size', '50'); // Always use 50 tickets per page
    
    const fullUrl = `${apiUrl}?${params.toString()}`;

    const response = await fetch(fullUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authToken ? `Bearer ${authToken}` : ''
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch page ${page}: ${response.status}`);
    }

    const responseData = await response.json();
    
    const ensureArray = (val: any): any[] => Array.isArray(val) ? val : val != null && typeof val === 'object' ? [val] : [];
    let tickets: any[] = [];
    let pageMeta = null;
    
    if (responseData.data !== undefined) {
      tickets = ensureArray(responseData.data);
      pageMeta = responseData.page_meta;
    } else if (responseData.results !== undefined) {
      tickets = ensureArray(responseData.results);
      pageMeta = responseData.page_meta;
    } else {
      throw new Error('Invalid data format received');
    }

    // Transform the data
    const transformedData = tickets.map(ticket => ({
      ...ticket,
      created_at: ticket.created_at ? convertGMTtoIST(ticket.created_at) : 'N/A',
      cse_name: getDisplayName(ticket.cse_name || ticket.assigned_to),
      name: ticket.first_name && ticket.last_name 
        ? `${ticket.first_name} ${ticket.last_name}`
        : ticket.name || 'N/A',
      reason: ticket.reason || ticket.Description || 'No reason provided',
      resolution_status: ticket.resolution_status || ticket.status || 'Open',
      poster: ticket.poster || 'No Poster',
      praja_dashboard_user_link: ticket.praja_user_id 
        ? `https://app.praja.com/dashboard/user/${ticket.praja_user_id}`
        : ticket.praja_dashboard_user_link || 'N/A',
      display_pic_url: ticket.display_pic_url || null
    }));

    setData(transformedData);
    setFilteredData(transformedData);
    
    if (pageMeta) {
      setPagination({
        totalCount: pageMeta.total_count || 0,
        numberOfPages: pageMeta.number_of_pages || 0,
        currentPage: pageMeta.current_page || 1,
        pageSize: pageMeta.page_size || 50,
        nextPageLink: pageMeta.next_page_link || null,
        previousPageLink: pageMeta.previous_page_link || null
      });
    }
  } catch (error) {
    console.error('Error fetching page:', error);
    toast.error(`Failed to load page ${page}`);
  } finally {
    setTableLoading(false);
  }
};

useRecordUpdated(
  (payload: RecordUpdatedPayload) => {
    if (!session?.access_token) return;

    const recordId = payload.record_id != null ? String(payload.record_id) : '';
    if (!recordId) return;

    const matches = (row: any) => {
      const id = row?.id != null ? String(row.id) : '';
      return id === recordId;
    };

    let found = false;
    const patchList = (prev: any[]) => {
      const idx = prev.findIndex(matches);
      if (idx < 0) return prev;
      found = true;
      const next = prev.slice();
      next[idx] = {
        ...prev[idx],
        ...(payload.assigned_to !== undefined ? { assigned_to: payload.assigned_to } : {}),
      };
      return next;
    };
    setFilteredData(patchList);
    setData(patchList);

    if (found || !payload.created) return;
    if (createdRefreshTimerRef.current != null) {
      window.clearTimeout(createdRefreshTimerRef.current);
    }
    createdRefreshTimerRef.current = window.setTimeout(() => {
      createdRefreshTimerRef.current = null;
      void applyFilters(undefined, { silent: true, keepPage: true });
    }, REALTIME_LIST_DEBOUNCE_MS);
  },
  { entityType: 'support_ticket' },
);

const handleTicketUpdate = (updatedTicket: any) => {
  // Update the local data with the updated ticket
  const updatedData = data.map(ticket => 
    ticket.id === updatedTicket.id ? updatedTicket : ticket
  );
  setData(updatedData);
  
  // If filters are applied, refresh filtered data
  if (filtersApplied) {
    applyFilters();
  } else {
    // If no filters applied, update filtered data with all tickets
    setFilteredData(updatedData);
  }
  
  setIsTicketModalOpen(false);
};

useEffect(() => {
  // Wait for the role, or a CSE would briefly see the unfiltered list.
  if (!membershipLoaded) return;
  if (requireFiltersToShow) {
    setLoading(false);
    if (filtersApplied) {
      void applyFilters(undefined, { silent: true, keepPage: true });
    } else {
      clearTicketList();
    }
    return;
  }

  // Abort controller for initial fetch
  const abortController = new AbortController();

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const authToken = session?.access_token;

      const endpoint = config?.apiEndpoint || '/api/tickets';
      // Page builder uses TableConfig for ticketTable (no apiPrefix UI). Support
      // ticket APIs live on the renderer backend, not Supabase edge functions.
      const useRenderer =
        apiPrefix === 'renderer' ||
        endpoint.includes('/support-ticket/');
      const baseUrl = useRenderer
        ? TICKET_API_BASE
        : import.meta.env.VITE_API_URI;
      const apiUrl = `${baseUrl}${endpoint}?page=1&page_size=50`;
      console.log('API URL:', apiUrl);
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Authorization': authToken ? `Bearer ${authToken}` : ''
      };

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers,
        signal: abortController.signal
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch tickets: ${response.status}`);
      }

      // Clone the response before reading it
      const responseClone = response.clone();
      const responseData = await responseClone.json();

      // Handle different response formats
      let tickets = [];
      let pageMeta = null;
      
      if (responseData.data && Array.isArray(responseData.data)) {
        tickets = responseData.data;
        pageMeta = responseData.page_meta;
      } else if (Array.isArray(responseData)) {
        tickets = responseData;
      } else if (responseData.tickets && Array.isArray(responseData.tickets)) {
        tickets = responseData.tickets;
      } else {
        throw new Error('Invalid data format received');
      }

      // Transform the data with the new attributes
      const transformedData = tickets.map((ticket: any) => ({
        ...ticket,
        // Format created_at with relative time
        created_at: ticket.created_at ? convertGMTtoIST(ticket.created_at) : 'N/A',
        // Use cse_name for assigned to with display name
        cse_name: getDisplayName(ticket.cse_name || ticket.assigned_to),
        // Combine first_name and last_name for name
        name: ticket.first_name && ticket.last_name 
          ? `${ticket.first_name} ${ticket.last_name}`
          : ticket.name || 'N/A',
        // Use reason field
        reason: ticket.reason || ticket.Description || 'No reason provided',
        // Use resolution_status with proper formatting
        resolution_status: ticket.resolution_status || ticket.status || 'Open',
        // Use poster field directly
        poster: ticket.poster || 'No Poster',
        // Generate Praja dashboard user link
        praja_dashboard_user_link: ticket.praja_user_id 
          ? `https://app.praja.com/dashboard/user/${ticket.praja_user_id}`
          : ticket.praja_dashboard_user_link || 'N/A',
        // Ensure display_pic_url is included
        display_pic_url: ticket.display_pic_url || null
      }));

      // Set the data (empty array if no tickets found)
      setData(transformedData);
      setFilteredData(transformedData);
      baseDataRef.current = transformedData;
      console.log('Data loaded. FiltersApplied:', filtersApplied, 'Data count:', transformedData.length);
      
      // Set pagination data if available
      if (pageMeta) {
        setPagination({
          totalCount: pageMeta.total_count || 0,
          numberOfPages: pageMeta.number_of_pages || 0,
          currentPage: pageMeta.current_page || 1,
          pageSize: pageMeta.page_size || 50,
          nextPageLink: pageMeta.next_page_link || null,
          previousPageLink: pageMeta.previous_page_link || null
        });
      }
      
      // If filters were previously applied, reapply them after data loads
      if (filtersApplied && (
        resolutionStatusFilter.length > 0 || 
        assignedToFilter !== 'all' || 
        posterStatusFilter.length > 0 || 
        stateFilter.length > 0 ||
        callAttemptsFilter.length > 0 ||
        dateRangeFilter.startDate || 
        dateRangeFilter.endDate || 
        searchTerm.trim() !== ''
      )) {
        console.log('Reapplying filters after data fetch...');
        setTimeout(() => applyFilters(), 100); // Small delay to ensure state is updated
      }
    } catch (error: any) {
      // Effect re-runs (token/config) abort the previous fetch — not a real failure.
      if (
        error?.name === 'AbortError' ||
        abortController.signal.aborted ||
        (typeof error?.message === 'string' &&
          error.message.toLowerCase().includes('aborted'))
      ) {
        return;
      }
      console.error('Error fetching tickets:', error);
      // Set empty data on error instead of using demo data
      setData([]);
      setFilteredData([]);
      
      // If filters were previously applied, we still need to maintain that state
      if (filtersApplied) {
        console.log('Filters were applied but data fetch failed');
      }
    } finally {
      if (!abortController.signal.aborted) {
        setLoading(false);
      }
    }
  };

  fetchTickets();

  return () => {
    abortController.abort();
  };
}, [session?.access_token, config?.apiEndpoint, apiPrefix, membershipLoaded, requireFiltersToShow]);

// Fetch filter options and assignees when component mounts
useEffect(() => {
  if (session?.access_token) {
    fetchFilterOptions();
    fetchAssignees();
  }
}, [session?.access_token]);

// Apply filters when filter values change
useEffect(() => {
  // Don't auto-apply filters, wait for user to click "Apply Filters" button
}, [resolutionStatusFilter, assignedToFilter, posterStatusFilter, stateFilter, callAttemptsFilter, data]);

// Update apiPrefix when config changes
useEffect(() => {
  if (config?.apiPrefix) {
    setApiPrefix(config.apiPrefix);
  }
}, [config?.apiPrefix]);

// Cleanup timeout and abort controller on unmount
useEffect(() => {
  return () => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    if (createdRefreshTimerRef.current != null) {
      window.clearTimeout(createdRefreshTimerRef.current);
      createdRefreshTimerRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };
}, []);

  return {
    config,
    loading,
    tableLoading,
    searchLoading,
    displaySearchTerm,
    showFilters,
    setShowFilters,
    resolutionStatusFilter,
    setResolutionStatusFilter,
    assignedToFilter,
    setAssignedToFilter,
    posterStatusFilter,
    setPosterStatusFilter,
    stateFilter,
    setStateFilter,
    callAttemptsFilter,
    setCallAttemptsFilter,
    dateRangeFilter,
    setDateRangeFilter,
    filtersApplied,
    requireFiltersToShow,
    filteredData,
    data,
    pagination,
    selectedTicket,
    setSelectedTicket,
    isTicketModalOpen,
    setIsTicketModalOpen,
    tableColumns,
    handleSearchChange,
    handleRowClick,
    handleTicketUpdate,
    handleActionClick,
    handlePageChange,
    handleNextPage,
    handlePreviousPage,
    applyFilters,
    resetFilters,
    getUniqueResolutionStatuses,
    getUniqueAssignedTo,
    getUniquePosterStatuses,
    apiPrefix,
    searchTerm,
    stateToParamValue,
  };
}

export type TicketTableModel = ReturnType<typeof useTicketTable>;
